import React, { useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Upload, 
  FileText, 
  Copy, 
  Check, 
  Loader2, 
  Sparkles,
  X,
  Settings2,
  Truck,
  Hash,
  MessageSquare,
  Image,
  ImagePlus,
  Download,
  RefreshCw,
  CheckCircle
} from "lucide-react";
import { useBusiness } from "@/contexts/BusinessContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DesignRentaTab() {
  const { currentBusiness, businessId, loading: businessLoading } = useBusiness();
  
  // State
  const [file, setFile] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState("");
  const [customModel, setCustomModel] = useState("");
  const [rentalPrice, setRentalPrice] = useState("");
  const [specifications, setSpecifications] = useState([]);
  
  // Vehicle image
  const [vehicleImage, setVehicleImage] = useState(null);
  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [generatingIndex, setGeneratingIndex] = useState(-1);
  
  // Generated content
  const [carousel, setCarousel] = useState([]);
  const [promptStories, setPromptStories] = useState("");
  const [copyFeed, setCopyFeed] = useState("");
  const [copyStories, setCopyStories] = useState("");
  const [hashtagsFeed, setHashtagsFeed] = useState([]);
  const [hashtagsStories, setHashtagsStories] = useState([]);
  
  // UI state
  const [copiedItem, setCopiedItem] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [activeTab, setActiveTab] = useState("feed");
  
  const fileInputRef = useRef(null);

  const handleFileSelect = async (selectedFile) => {
    if (!selectedFile) return;
    
    if (!businessId) {
      toast.error("Error: No se ha seleccionado un negocio.");
      return;
    }
    
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error("Tipo de archivo no soportado. Usa PDF, PNG, JPG o WEBP.");
      return;
    }

    setFile(selectedFile);
    setIsUploading(true);
    resetContent();

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      console.log("Upload response:", response.data);
      setFileInfo(response.data);
      toast.success("Archivo subido correctamente");
      await extractSpecs(response.data, businessId);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error("Error al subir el archivo");
      setFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const resetContent = () => {
    setExtractedData(null);
    setCarousel([]);
    setPromptStories("");
    setCopyFeed("");
    setCopyStories("");
    setHashtagsFeed([]);
    setHashtagsStories([]);
    setSpecifications([]);
    setSelectedVersion("");
    setCustomModel("");
    setVehicleImage(null);
    setGeneratedImages([]);
  };

  const extractSpecs = async (uploadData, currentBusinessId) => {
    setIsExtracting(true);
    try {
      console.log("Extracting specs with:", uploadData);
      const formData = new FormData();
      formData.append('file_id', uploadData.file_id);
      formData.append('filename', uploadData.filename);

      const response = await axios.post(`${API}/extract-specs`, formData);
      console.log("Extract response:", response.data);
      
      if (response.data.success) {
        const data = response.data.data;
        setExtractedData(data);
        
        // Save extracted vehicle image
        if (response.data.vehicle_image) {
          setVehicleImage(response.data.vehicle_image);
          toast.success("Imagen del equipo extraída automáticamente");
        }
        
        // Handle new format with separate versions
        if (data.versions) {
          const versionKeys = Object.keys(data.versions);
          if (versionKeys.length > 0) {
            const firstVersionKey = versionKeys[0];
            const firstVersion = data.versions[firstVersionKey];
            setSelectedVersion(firstVersion.name || firstVersionKey);
            setSpecifications(firstVersion.specifications || []);
          }
        } else if (data.specifications) {
          setSpecifications(data.specifications || []);
          if (data.available_versions?.length > 0) {
            setSelectedVersion(data.available_versions[0]);
          }
        }
        
        // Set model name
        if (data.model_name) {
          setCustomModel(data.model_name);
        }
        
        toast.success("Datos extraídos correctamente");
      } else {
        toast.error("No se pudieron extraer los datos. Intenta con otro archivo.");
      }
    } catch (error) {
      console.error('Extract error:', error);
      const errorMessage = error.response?.data?.detail || "Error al extraer especificaciones";
      if (errorMessage.includes("Unable to process")) {
        toast.error("No se pudo procesar la imagen. Intenta con un archivo PDF o una imagen más clara.");
      } else {
        toast.error("Error al extraer especificaciones. Verifica que el archivo sea válido.");
      }
    } finally {
      setIsExtracting(false);
    }
  };

  const generateContent = async () => {
    if (!extractedData && !customModel) {
      toast.error("Primero extrae los datos de la ficha técnica");
      return;
    }
    
    if (!customModel || specifications.length === 0) {
      toast.error("Completa el modelo y las especificaciones");
      return;
    }

    setIsGenerating(true);
    try {
      const modelName = customModel || extractedData?.model_name || "";
      const version = selectedVersion || extractedData?.version || "";
      
      // Usar el nuevo endpoint específico para RENTA
      const response = await axios.post(`${API}/generate-rental-prompt`, {
        model_name: modelName,
        version: version,
        rental_price: rentalPrice || "Consultar",
        specifications: specifications.slice(0, 9),
        business_id: businessId
      });

      if (response.data.success) {
        // Incluir la imagen Web al principio si existe
        const allImages = [];
        if (response.data.web_catalog) {
          allImages.push(response.data.web_catalog);
        }
        allImages.push(...(response.data.carousel || []));
        
        // Agregar Stories al final del carrusel para generación
        if (response.data.prompt_stories) {
          allImages.push({
            id: allImages.length,
            name: "Stories",
            description: "Formato vertical 9:16 para Instagram Stories",
            prompt: response.data.prompt_stories,
            aspectRatio: "9:16" // Marcar el aspect ratio diferente
          });
        }
        
        setCarousel(allImages);
        setPromptStories(response.data.prompt_stories || "");
        
        // El copy ya viene orientado a RENTA desde el backend
        setCopyFeed(response.data.copy_feed || "");
        setCopyStories(response.data.copy_stories || "");
        setHashtagsFeed(response.data.hashtags_feed || []);
        setHashtagsStories(response.data.hashtags_stories || "");
        
        toast.success("Contenido de RENTA generado exitosamente");
      } else {
        toast.error("Error al generar contenido");
      }
    } catch (error) {
      console.error('Generate error:', error);
      toast.error("Error al generar contenido de renta");
    } finally {
      setIsGenerating(false);
    }
  };

  // Generar todas las imágenes una por una (como en el diseño principal)
  const generateAllImages = async () => {
    if (!vehicleImage || carousel.length === 0) {
      toast.error("Se necesita la imagen del equipo y los prompts generados");
      return;
    }
    
    setIsGeneratingImages(true);
    setGeneratedImages([]);
    setGeneratingIndex(0);
    
    try {
      toast.info("Iniciando generación de imágenes...");
      
      // Generate images one by one to show progress
      const results = [];
      for (let i = 0; i < carousel.length; i++) {
        setGeneratingIndex(i);
        const item = carousel[i];
        const aspectRatio = item.aspectRatio || (item.name === "Stories" ? "9:16" : "4:5");
        
        toast.info(`Generando ${item.name}... (${i + 1}/${carousel.length})`);
        
        try {
          const response = await axios.post(`${API}/generate-image`, {
            prompt: item.prompt,
            aspect_ratio: aspectRatio,
            vehicle_image_base64: vehicleImage
          });
          
          results.push({
            index: i,
            name: item.name,
            ratio: aspectRatio,
            result: response.data
          });
          
          // Update state progressively
          setGeneratedImages([...results]);
          
        } catch (err) {
          results.push({
            index: i,
            name: item.name,
            ratio: aspectRatio,
            result: { success: false, error: err.message }
          });
          setGeneratedImages([...results]);
        }
        
        // Delay between requests
        if (i < carousel.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
      
      setGeneratingIndex(-1);
      toast.success("Generación de imágenes completada!");
      
    } catch (error) {
      console.error('Image generation error:', error);
      toast.error("Error al generar imágenes: " + error.message);
    } finally {
      setIsGeneratingImages(false);
      setGeneratingIndex(-1);
    }
  };

  // Regenerar una imagen específica
  const regenerateImage = async (index) => {
    if (!vehicleImage || carousel.length === 0) {
      toast.error("Faltan datos para regenerar");
      return;
    }
    
    const item = carousel[index];
    if (!item) return;
    
    const aspectRatio = item.aspectRatio || (item.name === "Stories" ? "9:16" : "4:5");
    
    toast.info(`Regenerando ${item.name}...`);
    setGeneratingIndex(index);
    
    try {
      const response = await axios.post(`${API}/generate-image`, {
        prompt: item.prompt,
        aspect_ratio: aspectRatio,
        vehicle_image_base64: vehicleImage
      });
      
      // Update only that image in the results
      const newImages = [...generatedImages];
      newImages[index] = {
        index: index,
        name: item.name,
        ratio: aspectRatio,
        result: response.data
      };
      setGeneratedImages(newImages);
      
      if (response.data.success) {
        toast.success(`${item.name} regenerada exitosamente!`);
      } else {
        toast.error(`Error al regenerar ${item.name}`);
      }
    } catch (err) {
      toast.error("Error: " + err.message);
    } finally {
      setGeneratingIndex(-1);
    }
  };

  const confirmContent = async () => {
    try {
      const modelName = customModel || extractedData?.model_name || "";
      const version = selectedVersion || extractedData?.version || "";
      const year = extractedData?.year || "";
      const brand = extractedData?.brand || modelName.split(" ")[0] || "";

      // Extraer web_image y gallery_images de las imágenes generadas
      // La primera imagen (Web Catálogo) se usa como web_image para el catálogo público
      // Las demás se guardan como gallery_images
      let webImage = "";
      const galleryImages = [];
      
      generatedImages.forEach((img) => {
        const imageUrl = img.result?.data?.url || img.result?.image_url || "";
        if (!imageUrl) return;
        
        // La imagen "Web Catálogo" es la principal para el catálogo público
        if (img.name === "Web Catálogo" || img.name === "Web" || img.name?.toLowerCase().includes("web")) {
          webImage = imageUrl;
        } else if (img.name !== "Stories") {
          // Las demás imágenes (excepto Stories) van a la galería
          galleryImages.push({
            name: img.name || `Imagen ${img.index + 1}`,
            url: imageUrl,
            type: "feed"
          });
        }
      });

      // Guardar en rental_vehicles con web_image y gallery_images
      const vehicleResponse = await axios.post(`${API}/rental-vehicles`, {
        business_id: businessId,
        brand: brand,
        model_name: modelName,
        version: version,
        year: year,
        rental_price: rentalPrice,
        specifications: specifications,
        vehicle_image: vehicleImage || "",
        web_image: webImage,
        gallery_images: galleryImages,
        available: true
      });

      const vehicleId = vehicleResponse.data.vehicle?.id;

      // Crear publicación de tipo rental - usar nuevo formato de imágenes
      const images = generatedImages.map((img) => {
        const imageUrl = img.result?.data?.url || img.result?.image_url || "";
        return {
          name: img.name || `Imagen ${img.index + 1}`,
          url: imageUrl,
          type: img.name === "Stories" ? "stories" : "feed"
        };
      }).filter(img => img.url); // Solo incluir imágenes que tienen URL

      await axios.post(`${API}/publications`, {
        business_id: businessId,
        vehicle_id: vehicleId,
        vehicle_name: `${modelName} ${version}`.trim(),
        publication_type: "rental",
        images: images,
        copy_feed: copyFeed,
        copy_stories: copyStories,
        hashtags_feed: hashtagsFeed,
        hashtags_stories: hashtagsStories,
        status: "pendiente"
      });

      toast.success("Contenido de renta confirmado y guardado");
      clearAll();
    } catch (error) {
      console.error('Confirm error:', error);
      toast.error("Error al confirmar contenido");
    }
  };

  const clearAll = () => {
    setFile(null);
    setFileInfo(null);
    resetContent();
  };

  const copyToClipboard = async (text, itemId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(itemId);
      setTimeout(() => setCopiedItem(""), 2000);
    } catch (err) {
      toast.error("Error al copiar");
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  }, [businessId]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Left Column - Input */}
      <div className="space-y-6">
        
        {/* File Upload */}
        <div className="glass-card rounded-lg p-6" data-testid="rental-upload-section">
          <h2 className="font-montserrat text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Truck className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
            Ficha Técnica - RENTA
            <span className="ml-2 px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded">
              Unidad para Renta
            </span>
            {file && (
              <button
                onClick={clearAll}
                className="ml-auto text-slate-400 hover:text-white text-sm flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                Limpiar
              </button>
            )}
          </h2>
          
          <div
            className={`upload-zone rounded-lg p-8 text-center cursor-pointer border-2 border-dashed transition-colors ${
              dragOver ? 'border-orange-500 bg-orange-500/10' : 'border-slate-700 hover:border-orange-500/50'
            } ${businessLoading || !businessId ? 'opacity-50 pointer-events-none' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !businessLoading && businessId && fileInputRef.current?.click()}
            data-testid="rental-file-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp"
              onChange={(e) => handleFileSelect(e.target.files[0])}
              className="hidden"
              disabled={businessLoading || !businessId}
              data-testid="rental-file-input"
            />
            
            {isUploading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
                <p className="text-slate-400">Subiendo archivo...</p>
              </div>
            ) : file ? (
              <div className="flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <FileText className="w-7 h-7 text-orange-500" strokeWidth={1.5} />
                </div>
                <p className="text-white font-medium">{file.name}</p>
                <p className="text-slate-500 text-sm">Haz clic para cambiar</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-lg border-2 border-dashed border-slate-700 flex items-center justify-center">
                  <Upload className="w-7 h-7 text-slate-500" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-white font-medium">Arrastra tu archivo aquí</p>
                  <p className="text-slate-500 text-sm mt-1">PDF, PNG, JPG o WEBP</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Extracted Data Section */}
        {(isExtracting || extractedData) && (
          <div className="glass-card rounded-lg p-6" data-testid="rental-extracted-section">
            <h3 className="font-montserrat text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-orange-500" />
              Datos Extraídos - Renta
            </h3>
            
            {isExtracting ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                <span className="ml-3 text-slate-400">Extrayendo datos...</span>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Model Input */}
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Modelo</label>
                  <input
                    type="text"
                    value={customModel}
                    onChange={(e) => setCustomModel(e.target.value)}
                    placeholder="Nombre del modelo"
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-orange-500 focus:outline-none"
                    data-testid="rental-model-input"
                  />
                </div>

                {/* Version Selection */}
                {extractedData?.available_versions?.length > 0 && (
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Versión</label>
                    <select
                      value={selectedVersion}
                      onChange={(e) => setSelectedVersion(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                      data-testid="rental-version-select"
                    >
                      <option value="">Seleccionar versión</option>
                      {extractedData.available_versions.map((v, i) => (
                        <option key={i} value={v}>{v}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Rental Price Input */}
                <div>
                  <label className="block text-sm text-slate-400 mb-1">
                    Precio de Renta Mensual
                    <span className="ml-2 text-orange-400 text-xs">(MXN/mes)</span>
                  </label>
                  <input
                    type="text"
                    value={rentalPrice}
                    onChange={(e) => setRentalPrice(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="Ej: 50000"
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-orange-500 focus:outline-none"
                    data-testid="rental-price-input"
                  />
                  {rentalPrice && (
                    <p className="mt-1 text-sm text-green-400">
                      Renta mensual: ${parseInt(rentalPrice).toLocaleString('es-MX')} MXN
                    </p>
                  )}
                </div>

                {/* Specifications */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm text-slate-400">Especificaciones</label>
                    <span className="text-xs text-slate-500">{specifications.length} items</span>
                  </div>
                  <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 max-h-48 overflow-y-auto">
                    {specifications.length > 0 ? (
                      <ul className="space-y-1">
                        {specifications.map((spec, idx) => (
                          <li key={idx} className="text-sm text-slate-300 flex items-center justify-between gap-2 group hover:bg-slate-700/50 rounded px-1 py-0.5">
                            <span className="flex items-start gap-2 flex-1">
                              <span className="text-orange-500">•</span>
                              {spec}
                            </span>
                            <button
                              onClick={() => {
                                setSpecifications(prev => prev.filter((_, i) => i !== idx));
                              }}
                              className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-all"
                              title="Eliminar especificación"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-slate-500 text-sm">Sin especificaciones</p>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">Haz clic en la X para eliminar especificaciones que no necesites</p>
                </div>

                {/* Generate Button */}
                <button
                  onClick={generateContent}
                  disabled={isGenerating || !customModel}
                  className="w-full py-3 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                  data-testid="rental-generate-btn"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generando contenido...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generar Contenido de Renta
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Vehicle Image Preview */}
        {vehicleImage && (
          <div className="glass-card rounded-lg p-6">
            <h3 className="font-montserrat text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Image className="w-5 h-5 text-orange-500" />
              Imagen del Equipo
            </h3>
            <img 
              src={vehicleImage} 
              alt="Equipo de renta" 
              className="w-full max-h-64 object-contain rounded-lg"
            />
          </div>
        )}
      </div>

      {/* Right Column - Generated Content */}
      <div className="space-y-6">
        {carousel.length > 0 ? (
          <>
            {/* Carousel Section - Mostrar todas las imágenes generadas */}
            <div className="glass-card rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-montserrat text-lg font-semibold text-white flex items-center gap-2">
                  <ImagePlus className="w-5 h-5 text-orange-500" />
                  Imágenes ({carousel.length} prompts)
                </h3>
                <button
                  onClick={generateAllImages}
                  disabled={isGeneratingImages || !vehicleImage}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-orange-600 disabled:opacity-50"
                  data-testid="rental-generate-all-images-btn"
                >
                  {isGeneratingImages ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <ImagePlus className="w-4 h-4" />
                      Generar todas
                    </>
                  )}
                </button>
              </div>

              {/* Progress indicator */}
              {isGeneratingImages && generatingIndex >= 0 && (
                <div className="mb-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                    <span className="text-orange-400 text-sm">
                      Generando {carousel[generatingIndex]?.name}... ({generatingIndex + 1}/{carousel.length})
                    </span>
                  </div>
                  <div className="mt-2 w-full bg-slate-700 rounded-full h-2">
                    <div 
                      className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${((generatingIndex + 1) / carousel.length) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Generated Images Grid */}
              {generatedImages.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {generatedImages.map((img, idx) => {
                    const imageUrl = img.result?.success ? (img.result?.data?.url || img.result?.image_url) : null;
                    return (
                      <div key={idx} className={`p-4 rounded-lg border ${
                        img.result?.success && imageUrl 
                          ? 'bg-green-500/5 border-green-500/30' 
                          : 'bg-red-500/10 border-red-500/30'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white font-medium">{img.name}</span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            img.result?.success && imageUrl
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {img.result?.success && imageUrl ? 'Generada' : 'Error'}
                          </span>
                        </div>
                        
                        {img.result?.success && imageUrl ? (
                          <div className="mt-3">
                            <img 
                              src={imageUrl}
                              alt={img.name}
                              className={`w-full rounded-lg border border-slate-700 ${
                                img.name === "Stories" ? "max-h-80 object-contain" : ""
                              }`}
                            />
                            <div className="flex gap-2 mt-2">
                              <a
                                href={imageUrl}
                                download={`${img.name.replace(/\s/g, '_')}_renta.png`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 py-2 rounded flex items-center justify-center gap-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors text-sm"
                              >
                                <Download className="w-4 h-4" />
                                Descargar
                              </a>
                              <button
                                onClick={() => regenerateImage(idx)}
                                disabled={generatingIndex !== -1}
                                className="flex-1 py-2 rounded flex items-center justify-center gap-2 bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 transition-colors text-sm disabled:opacity-50"
                                data-testid={`regenerate-btn-${idx}`}
                              >
                                {generatingIndex === idx ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-4 h-4" />
                                )}
                                Regenerar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2">
                            <div className="p-3 bg-red-500/10 rounded mb-2">
                              <p className="text-red-400 text-sm">
                                {img.result?.error || 'Error: No se pudo generar la imagen'}
                              </p>
                            </div>
                            <button
                              onClick={() => regenerateImage(idx)}
                              disabled={generatingIndex !== -1}
                              className="w-full py-2 rounded flex items-center justify-center gap-2 bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 transition-colors text-sm disabled:opacity-50"
                            >
                              {generatingIndex === idx ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4" />
                              )}
                              Reintentar
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <ImagePlus className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Haz clic en "Generar todas" para crear las imágenes</p>
                  <p className="text-xs mt-1">Se generarán: {carousel.map(c => c.name).join(", ")}</p>
                </div>
              )}
            </div>

            {/* Copy Section */}
            <div className="glass-card rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <button
                  onClick={() => setActiveTab("feed")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === "feed" 
                      ? "bg-orange-500 text-white" 
                      : "bg-slate-800 text-slate-400 hover:text-white"
                  }`}
                >
                  Feed
                </button>
                <button
                  onClick={() => setActiveTab("stories")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === "stories" 
                      ? "bg-orange-500 text-white" 
                      : "bg-slate-800 text-slate-400 hover:text-white"
                  }`}
                >
                  Stories
                </button>
              </div>

              {activeTab === "feed" ? (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm text-slate-400">Copy Feed</label>
                      <button
                        onClick={() => copyToClipboard(copyFeed, "copy-feed")}
                        className="text-slate-400 hover:text-white text-xs flex items-center gap-1"
                      >
                        {copiedItem === "copy-feed" ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                        {copiedItem === "copy-feed" ? "Copiado" : "Copiar"}
                      </button>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3 max-h-40 overflow-y-auto">
                      <p className="text-slate-300 text-sm whitespace-pre-wrap">{copyFeed}</p>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm text-slate-400">Hashtags Feed</label>
                      <button
                        onClick={() => copyToClipboard(hashtagsFeed.join(" "), "hashtags-feed")}
                        className="text-slate-400 hover:text-white text-xs flex items-center gap-1"
                      >
                        {copiedItem === "hashtags-feed" ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                        {copiedItem === "hashtags-feed" ? "Copiado" : "Copiar"}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {hashtagsFeed.map((tag, idx) => (
                        <span key={idx} className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded text-sm">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm text-slate-400">Copy Stories</label>
                      <button
                        onClick={() => copyToClipboard(copyStories, "copy-stories")}
                        className="text-slate-400 hover:text-white text-xs flex items-center gap-1"
                      >
                        {copiedItem === "copy-stories" ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                        {copiedItem === "copy-stories" ? "Copiado" : "Copiar"}
                      </button>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3 max-h-40 overflow-y-auto">
                      <p className="text-slate-300 text-sm whitespace-pre-wrap">{copyStories}</p>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm text-slate-400">Hashtags Stories</label>
                      <button
                        onClick={() => copyToClipboard(hashtagsStories.join(" "), "hashtags-stories")}
                        className="text-slate-400 hover:text-white text-xs flex items-center gap-1"
                      >
                        {copiedItem === "hashtags-stories" ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                        {copiedItem === "hashtags-stories" ? "Copiado" : "Copiar"}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {hashtagsStories.map((tag, idx) => (
                        <span key={idx} className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded text-sm">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Button */}
            {generatedImages.length > 0 && (
              <div className="flex justify-center">
                <button
                  onClick={confirmContent}
                  className="px-8 py-4 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold uppercase tracking-wider flex items-center gap-3 hover:opacity-90 transition-opacity"
                  data-testid="rental-confirm-btn"
                >
                  <CheckCircle className="w-5 h-5" />
                  Confirmar Contenido de Renta
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="glass-card rounded-lg p-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-lg border border-slate-800 flex items-center justify-center">
                  <Truck className="w-8 h-8 text-slate-700" strokeWidth={1} />
                </div>
                <p className="text-slate-600 font-medium">Esperando datos...</p>
                <p className="text-slate-700 text-sm mt-1">
                  Sube una ficha técnica para crear contenido de renta
                </p>
                <div className="mt-4 px-4 py-2 bg-orange-500/10 text-orange-400 rounded-lg text-sm">
                  Las unidades de renta aparecerán en el catálogo público
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DesignRentaTab;
